import React, { useState, useEffect } from 'react';
import { Copy, RefreshCw, Wand2, Check, Sparkles, Camera, Box, PenTool, Rocket, ShoppingBag, AlertTriangle } from 'lucide-react';

// --- COMPONENTES UI ---
const HybridInput = ({ label, options, value, onChange, placeholder = "Escribe o selecciona..." }) => {
  return (
    <div className="mb-5">
      <div className="flex justify-between items-center mb-1">
        <label className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</label>
      </div>
      <div className="flex gap-2">
        <div className="relative w-1/3 min-w-[120px]">
          <select
            className="w-full h-full appearance-none bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all cursor-pointer hover:bg-gray-750"
            onChange={(e) => { if (e.target.value) onChange(e.target.value); }}
            value="" 
          >
            <option value="" disabled>Sugerencias...</option>
            {options && options.map((opt, idx) => (
              <option key={idx} value={opt}>{opt.substring(0, 35)}...</option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
            <Sparkles size={14} className="text-indigo-400" />
          </div>
        </div>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-gray-900 border border-gray-700 text-white text-sm rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all placeholder-gray-600"
        />
      </div>
    </div>
  );
};

export default function SuperPromptApp() {
  const [dataCategories, setDataCategories] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  const [categoryKey, setCategoryKey] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [variables, setVariables] = useState({});
  const [finalPrompt, setFinalPrompt] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Estado para controlar si el usuario ha editado manualmente el prompt
  const [isManuallyEdited, setIsManuallyEdited] = useState(false);

  const ICON_MAP = {
    shoppingBag: <ShoppingBag size={20} />,
    camera: <Camera size={20} />,
    box: <Box size={20} />,
    penTool: <PenTool size={20} />,
    rocket: <Rocket size={20} />
  };

  // --- FETCH MEJORADO (Anti-Cache) ---
  useEffect(() => {
    fetch('./data.json?v=' + new Date().getTime())
      .then(response => {
        if (!response.ok) {
          throw new Error(`Error ${response.status}: No se encontró data.json`);
        }
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new TypeError("El archivo recibido no es JSON (Posiblemente es HTML de error 404)");
        }
        return response.json();
      })
      .then(jsonData => {
        setDataCategories(jsonData);
        const firstCatKey = Object.keys(jsonData)[0];
        if(firstCatKey) {
            const firstTemplateId = jsonData[firstCatKey].templates[0].id;
            setCategoryKey(firstCatKey);
            setTemplateId(firstTemplateId);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("Fallo crítico:", err);
        setErrorMsg(err.message);
        setLoading(false);
      });
  }, []);

  const activeCategory = dataCategories ? dataCategories[categoryKey] : null;
  const activeTemplate = activeCategory 
    ? (activeCategory.templates.find(t => t.id === templateId) || activeCategory.templates[0]) 
    : null;

  const handleTemplateChange = (newTemplateId) => {
    setTemplateId(newTemplateId);
    setVariables({}); 
    setIsManuallyEdited(false); // Reiniciamos edición manual al cambiar template
  };

  const handleCategoryChange = (key) => {
    setCategoryKey(key);
    const firstTemplate = dataCategories[key].templates[0];
    setTemplateId(firstTemplate.id);
    setVariables({});
    setIsManuallyEdited(false);
  };

  // Generar Prompt en tiempo real (Solo si no ha sido editado manualmente)
  useEffect(() => {
    if (!activeTemplate || !activeCategory || isManuallyEdited) return;

    const base = activeTemplate.base;
    const cleanSubject = subject.trim() ? subject : "[SUJETO]";
    const variablesText = activeCategory.variableSchema
      .map(schemaItem => variables[schemaItem.id])
      .filter(val => val && val.trim().length > 0)
      .join(", ");
    
    let prompt = base.replace('[SUJETO]', cleanSubject);
    if (variablesText.length > 0) {
      if (prompt.endsWith('.')) prompt = prompt.slice(0, -1);
      prompt = `${prompt}, ${variablesText}.`;
    }
    setFinalPrompt(prompt);
  }, [categoryKey, templateId, subject, variables, activeCategory, activeTemplate, isManuallyEdited]);

  const handleCopy = () => {
    navigator.clipboard.writeText(finalPrompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // --- PANTALLAS DE ESTADO ---
  if (loading) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center text-white">
      <div className="flex flex-col items-center gap-4 animate-pulse">
        <Wand2 size={40} className="text-indigo-500" />
        <p className="text-lg font-light">Cargando Inteligencia...</p>
      </div>
    </div>
  );

  if (errorMsg) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center text-white p-4">
      <div className="bg-red-900/20 border border-red-500/50 p-8 rounded-2xl max-w-md text-center">
        <AlertTriangle size={48} className="text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold mb-2 text-red-400">Error de Conexión</h2>
        <p className="text-gray-300 mb-6">{errorMsg}</p>
        <button onClick={() => window.location.reload()} className="bg-red-600 px-6 py-2 rounded-lg hover:bg-red-500 transition-colors">
          Reintentar
        </button>
      </div>
    </div>
  );

  // --- APP PRINCIPAL ---
  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 font-sans selection:bg-indigo-500 selection:text-white">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-indigo-600 p-2 rounded-lg">
              <Wand2 size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-white leading-none">Súper Prompt</h1>
              <span className="text-xs text-indigo-400 font-medium tracking-wide">GENERATOR v4.0</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* COLUMNA IZQUIERDA */}
        <div className="lg:col-span-7 space-y-8">
          
          {/* 1. Categorías */}
          <section>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 block">1. Elige tu Categoría</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {dataCategories && Object.entries(dataCategories).map(([key, cat]) => (
                <button
                  key={key}
                  onClick={() => handleCategoryChange(key)}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all duration-200 ${
                    categoryKey === key
                      ? 'bg-indigo-600/10 border-indigo-500 text-indigo-400 shadow-[0_0_15px_rgba(99,102,241,0.3)]'
                      : 'bg-gray-900 border-gray-800 text-gray-400 hover:border-gray-600 hover:bg-gray-800'
                  }`}
                >
                  <div className={`mb-1 ${categoryKey === key ? 'text-indigo-400' : 'text-gray-500'}`}>
                    {ICON_MAP[cat.icon] || <Sparkles size={20} />}
                  </div>
                  <span className="text-[10px] font-bold uppercase text-center">{cat.label.split(" ")[0]}</span>
                </button>
              ))}
            </div>
          </section>

          {/* 2. Plantillas */}
          {activeTemplate && (
          <section>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 block">2. Selecciona el Estilo Base</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {activeCategory.templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleTemplateChange(template.id)}
                  className={`text-left px-4 py-3 rounded-lg border transition-all ${
                    templateId === template.id
                      ? 'bg-gray-800 border-indigo-500 ring-1 ring-indigo-500/50 text-white'
                      : 'bg-gray-900 border-gray-800 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  <div className="text-sm font-semibold">{template.name}</div>
                  <div className="text-xs text-gray-500 truncate mt-1 opacity-70">
                    {template.base.replace('[SUJETO]', '...')}
                  </div>
                </button>
              ))}
            </div>
          </section>
          )}

          {/* 3. Sujeto */}
          <section className="bg-gray-900/50 p-5 rounded-2xl border border-gray-800">
            <label className="flex items-center gap-2 text-sm font-bold text-white mb-3">
              <span className="bg-indigo-600 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">3</span>
              ¿Qué quieres crear?
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                setIsManuallyEdited(false); // Si cambia el sujeto, permitimos que la IA regenere el prompt
              }}
              placeholder={activeTemplate?.suggestion || "Ej: Un gato astronauta..."}
              className="w-full bg-gray-950 border border-gray-700 text-white text-lg rounded-xl px-4 py-4 focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </section>

          {/* 4. Variables */}
          {activeCategory && (
          <section className="bg-gray-900/30 p-5 rounded-2xl border border-gray-800/50">
            <div className="flex items-center justify-between mb-5">
              <label className="flex items-center gap-2 text-sm font-bold text-white">
                <span className="bg-gray-700 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">4</span>
                Personalización Contextual
              </label>
              <button onClick={() => { setVariables({}); setIsManuallyEdited(false); }} className="text-xs text-gray-500 hover:text-white flex items-center gap-1">
                <RefreshCw size={12} /> Limpiar
              </button>
            </div>
            <div className="space-y-1">
              {activeCategory.variableSchema.map((schemaItem) => {
                const contextualOptions = activeTemplate.presets ? activeTemplate.presets[schemaItem.id] : [];
                return (
                  <HybridInput
                    key={`${categoryKey}-${templateId}-${schemaItem.id}`}
                    label={schemaItem.label}
                    options={contextualOptions || []}
                    value={variables[schemaItem.id]}
                    onChange={(val) => {
                        setVariables({ ...variables, [schemaItem.id]: val });
                        setIsManuallyEdited(false);
                    }}
                    placeholder={`Detalles...`}
                  />
                );
              })}
            </div>
          </section>
          )}
        </div>

        {/* COLUMNA DERECHA */}
        <div className="lg:col-span-5">
          <div className="sticky top-28 space-y-4">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700 shadow-2xl overflow-hidden relative">
              <div className="bg-gray-950/50 px-4 py-3 border-b border-gray-700 flex justify-between items-center">
                <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider flex items-center gap-2">
                  <Sparkles size={14} /> Prompt Final
                </span>
                {isManuallyEdited && (
                    <span className="text-[10px] text-amber-500 bg-amber-900/30 px-2 py-0.5 rounded-full border border-amber-500/30">
                        Editado Manualmente
                    </span>
                )}
              </div>
              <div className="p-5">
                {/* TEXTAREA EDITABLE */}
                <textarea
                  value={finalPrompt}
                  onChange={(e) => {
                    setFinalPrompt(e.target.value);
                    setIsManuallyEdited(true); // Bloquea la regeneración automática
                  }}
                  className="w-full h-48 bg-transparent text-lg text-gray-200 resize-none outline-none leading-relaxed font-medium placeholder-gray-600"
                  placeholder="Aquí aparecerá tu Súper Prompt..."
                />
              </div>
              <div className="p-4 bg-gray-950/30 border-t border-gray-700">
                {/* BOTÓN CON PROTECCIÓN DE TRADUCCIÓN */}
                <button
                  onClick={handleCopy}
                  className={`w-full py-4 rounded-xl font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-all transform active:scale-95 ${
                    copied
                      ? 'bg-green-500 text-white shadow-[0_0_20px_rgba(34,197,94,0.4)]'
                      : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-[0_0_20px_rgba(99,102,241,0.4)]'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check size={18} /> 
                      <span className="notranslate">¡Copiado!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={18} /> 
                      <span className="notranslate">Copiar Prompt</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            
            <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
              <h3 className="text-xs font-bold text-gray-400 uppercase mb-2">Recomendación Contextual:</h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                Estás creando un estilo <strong>{activeTemplate?.name}</strong>.
                {isManuallyEdited 
                    ? " Has editado el texto manualmente, por lo que las selecciones de arriba ya no actualizarán este prompt."
                    : " Las sugerencias de arriba han sido filtradas específicamente para este estilo."
                }
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}